/**
 * Sub-components for SpeakingInterface
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { SpeakingMode } from './SpeakingInterface.helpers';
import { formatTime } from './SpeakingInterface.helpers';
import { formatTimerAriaLabel } from '@/utils/timerAccessibility';

interface QuestionContentProps {
    readonly mode: SpeakingMode;
    readonly prompt: string;
    readonly contextText: string | null;
    readonly imageSrc: string | null;
}

export function QuestionContent({ mode, prompt, contextText, imageSrc }: QuestionContentProps) {
    const { t } = useTranslation();

    const isAudioMode = mode === 'section-audio' || mode === 'context-audio';

    if (isAudioMode) {
        return (
            <div className="mb-6 sm:mb-8">
                <p className="font-reading text-sm sm:text-base text-muted-foreground leading-relaxed text-center">
                    {mode === 'section-audio'
                        ? t('simulation.speaking.listeningToIntroduction', { defaultValue: 'Please listen to the section introduction...' })
                        : t('simulation.speaking.listeningToContext', { defaultValue: 'Please listen to the question context...' })}
                </p>
                {contextText && mode === 'context-audio' && (
                    <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3 sm:p-4 text-xs sm:text-sm text-foreground shadow-sm">
                        <p className="whitespace-pre-line leading-relaxed">{contextText}</p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="mb-6 sm:mb-8">
            <p className="font-reading text-base sm:text-lg text-foreground leading-relaxed text-center mb-3 sm:mb-4 whitespace-pre-line">
                {prompt}
            </p>
            {contextText && (
                <div className="mb-4 rounded-lg border border-border bg-muted/30 p-3 sm:p-4 text-xs sm:text-sm text-foreground shadow-sm">
                    <p className="whitespace-pre-line leading-relaxed">{contextText}</p>
                </div>
            )}
            {imageSrc && (
                <div className="flex justify-center mt-4 sm:mt-6">
                    <img
                        src={imageSrc}
                        alt="Question illustration"
                        className="max-w-full max-h-48 sm:max-h-64 rounded-lg border border-border shadow-md object-contain"
                    />
                </div>
            )}
        </div>
    );
}

interface TimerDisplayProps {
    readonly mode: SpeakingMode;
    readonly prepTimeLeft: number;
    readonly recordTimeLeft: number;
    readonly recordedDurationSeconds: number;
}

export function TimerDisplay({ mode, prepTimeLeft, recordTimeLeft, recordedDurationSeconds }: TimerDisplayProps) {
    const { t } = useTranslation();
    const timerUnitLabels = {
        minute: t('simulation.active.timerUnits.minute', { defaultValue: 'minute' }),
        minutes: t('simulation.active.timerUnits.minutes', { defaultValue: 'minutes' }),
        second: t('simulation.active.timerUnits.second', { defaultValue: 'second' }),
        seconds: t('simulation.active.timerUnits.seconds', { defaultValue: 'seconds' }),
    };
    const prepAriaLabel = formatTimerAriaLabel(prepTimeLeft, {
        prefix: t('simulation.speaking.prepTimerAriaPrefix', { defaultValue: 'Time to think remaining' }),
        ...timerUnitLabels,
    });
    const recordingAriaLabel = formatTimerAriaLabel(recordTimeLeft, {
        prefix: t('simulation.speaking.recordingTimerAriaPrefix', { defaultValue: 'Recording time remaining' }),
        ...timerUnitLabels,
    });

    const showPrepTimer = mode === 'prep';
    const showRecordingTimer = mode === 'recording' || mode === 'done';

    return (
        <div className="flex justify-center gap-4 sm:gap-8 mb-6 sm:mb-8">
            {/* Prep Timer — visible only during preparation */}
            {showPrepTimer && (
                <div
                    role="timer"
                    aria-live="polite"
                    aria-atomic="true"
                    aria-label={prepAriaLabel}
                    className="text-center"
                >
                    <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1 font-ui">
                        {t('simulation.speaking.timeToThink', { defaultValue: 'Think' })}
                    </div>
                    <div className="text-2xl sm:text-4xl font-bold tabular-nums font-ui text-exam-prep">
                        {formatTime(prepTimeLeft)}
                    </div>
                </div>
            )}

            {/* Recording Timer — visible during recording and after completion */}
            {showRecordingTimer && (
                <div
                    role="timer"
                    aria-live="polite"
                    aria-atomic="true"
                    aria-label={recordingAriaLabel}
                    className="text-center"
                >
                    <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1 font-ui">
                        {t('simulation.speaking.recordingLabel', { defaultValue: 'Rec' })}
                    </div>
                    <div
                        className={cn(
                            "text-2xl sm:text-4xl font-bold tabular-nums font-ui",
                            mode === 'recording' ? "text-exam-recording" : "text-foreground/75"
                        )}
                    >
                        {formatTime(mode === 'done' ? recordedDurationSeconds : recordTimeLeft)}
                    </div>
                </div>
            )}
        </div>
    );
}
